<template>
    <q-dialog
        v-model="showModal"
        :maximized="$q.screen.lt.md"
    >
        <q-card :style="$q.screen.gt.sm ? 'width: 700px; max-width: 80vw;' : ''">
            <q-card-section class="row items-center q-pb-none" dir="ltr">
                <q-item>
                    <q-item-section>
                        <q-item-label lines="2" class="text-h6">
                            1 {{displayable_origin_symbol}} =
                            <currency-format
                                :value="currentRate"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display:inline-block"
                                :unit="displayable_dest_symbol"
                            />
                        </q-item-label>
                        <q-item-label caption>Updated {{elapsedTime}}</q-item-label>
                    </q-item-section>
                </q-item>
                <q-space />
                <q-btn icon="close" flat round dense v-close-popup />
            </q-card-section>

            <q-card-section :class="$q.screen.gt.sm ? 'q-pt-none' : 'q-pa-none'" class="q-pt-md" dir="ltr">
                <q-inner-loading :showing="loading">
                    <q-spinner-gears size="50px" color="primary" />
                </q-inner-loading>

                <q-tabs
                    v-model="selection"
                    no-caps
                    dense
                >
                    <q-tab name="one_week" label="1W" @click="updateData('one_week')"/>
                    <q-tab name="one_month" label="1M" @click="updateData('one_month')"/>
                    <q-tab name="six_month" label="6M" @click="updateData('six_month')"/>
                </q-tabs>

                <div id="rate-chart" class="q-mt-lg">
                    <apexchart
                        ref="chartRef"
                        height="350"
                        type="area"
                        :options="chartOptions"
                        :series="series"
                        :disabled="loading"
                    />
                </div>
            </q-card-section>

            <q-card-actions align="right" class="text-primary">
                <q-btn flat :label="$t('close')" v-close-popup />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>
<script>

import Rate from "../models/Rate"

export default {
    name: "RateChartDialog",
    model: {
        prop: 'open',
        event: 'change'
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        from: {
            type: Object,
            required: true
        },
        to: {
            type: Object,
            required: true
        },
        decimals: {
            type: Number,
            default: 2
        },
        rateReversed: {
            type: Boolean,
            default: false
        },
        currentRate: {
            type: Number|String,
            default: 0
        },
        elapsedTime: {
            type: String,
            default: ''
        }
    },
    computed: {
        showModal: {
            get: function () {
                return this.open
            },
            set: function (value) {
                this.$emit('change', value)
            }
        },
        series() {
            return [
                {
                    name: 'Rate',
                    data: this.data.map((item) => {
                        return [item.time, item.rate < 0.1 ? (1 / item.rate).roundToX(this.decimals) : item.rate.roundToX(this.decimals)]
                    })
                }
            ]
        },
        displayable_origin_symbol() {
            return this.rateReversed ? this.to.symbol : this.from.symbol
        },
        displayable_dest_symbol() {
            return this.rateReversed ? this.from.symbol : this.to.symbol
        },
    },
    data() {
        return {
            data: [],
            times: {
                start: undefined,
                week: undefined,
                month: undefined,
                end: undefined
            },
            loading: false,
            selection: 'six_month',
            chartOptions: {
                series:[],
                chart: {
                    id: 'rate-chart',
                    type: 'area',
                    height: 350,
                    zoom: {
                        autoScaleYaxis: true
                    },
                    events:{
                        beforeResetZoom: () => {
                            this.selection = 'six_month'
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                markers: {
                    size: 0,
                    style: 'hollow',
                },
                colors: ['#4a148c', '#c2185b', '#e53935'],
                xaxis: {
                    type: 'datetime',
                    tickAmount: 6
                },
                tooltip: {
                    x: {
                        format: 'dd MMM yyyy'
                    }
                },
                plotOptions: {
                    area: {
                        fillTo: 'origin',
                    }
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.9,
                        stops: [0, 100]
                    }
                },
                stroke: {
                    width: 2,
                    curve: 'straight',
                    lineCap: 'round',
                },
                yaxis: {
                    decimalsInFloat: 2,
                    labels: {
                        style: {
                            fontSize: '10px',
                        },
                    }
                },
                responsive: [
                    {
                        breakpoint: 1000,
                        options: {
                            plotOptions: {
                                bar: {
                                    horizontal: false
                                }
                            },
                            legend: {
                                position: "bottom"
                            }
                        }
                    }
                ],
                selection: 'six_month',
            },
        }
    },
    methods: {
        loadRateData() {
            this.loading = true
            Rate.dailyRates(`from=${this.from.acronym}&to=${this.to.acronym}`).then(({data, start, week, month, end}) => {
                this.loading = false
                this.data = data
                this.times = {start, week, month, end}
                this.$refs.chartRef.updateOptions({
                    xaxis: {
                        min: start,
                        tickAmount: 6
                    },
                    yaxis: {
                        decimalsInFloat: this.decimals
                    }
                })
            }).catch(error => {
                this.loading = false
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
            })
        },
        updateData: function(timeline) {
            this.selection = timeline

            switch (timeline) {
                case 'one_week':
                    this.$refs.chartRef.zoomX(
                        this.times.week,
                        this.times.end
                    )
                    break
                case 'one_month':
                    this.$refs.chartRef.zoomX(
                        this.times.month,
                        this.times.end
                    )
                    break
                case 'six_month':
                    this.$refs.chartRef.zoomX(
                        this.times.start,
                        this.times.end
                    )
                    break
                case 'all':
                    this.$refs.chartRef.zoomX(
                        this.times.start,
                        this.times.end
                    )
                    break
                default:
            }
        }
    },
    updated() {},
    created() {},
    mounted() {},
    watch: {
        open(val) {
            if(val) {
                this.loadRateData()
            }
        }
    },
    components: {},
}
</script>
<style>

</style>
